import React, { useState, useEffect } from 'react';
import superAdminApiClient from '../../../api/superAdminApiClient';
import { toast } from 'react-toastify';
import { Modal, Button, Form, Table } from 'react-bootstrap';
import { formatPrice } from '../../../utils/priceUtils';

const PlanListing = () => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [currentPlan, setCurrentPlan] = useState({
        name: '',
        price: '',
        duration_days: '',
        is_trial: false,
        features: '',
        status: 'active',
        leads_included: ''
    });
    const [isEdit, setIsEdit] = useState(false);
    const [validated, setValidated] = useState(false);

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            const response = await superAdminApiClient.get('/superadmin/plans');
            setPlans(response.data.plans);
            setLoading(false);
        } catch (error) {
            toast.error("Failed to fetch plans");
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setValidated(true);

        // Validation
        if (!currentPlan.name || currentPlan.name.trim() === '') {
            toast.error("Plan name is required");
            return;
        }
        if (currentPlan.price === '' || currentPlan.price === null || currentPlan.price === undefined) {
            toast.error("Price is required");
            return;
        }
        if (!currentPlan.duration_days || currentPlan.duration_days <= 0) {
            toast.error("Duration must be at least 1 day");
            return;
        }
        if (currentPlan.leads_included === '' || currentPlan.leads_included === null || currentPlan.leads_included === undefined) {
            toast.error("Included leads is required");
            return;
        }

        try {
            const payload = {
                ...currentPlan,
                features: typeof currentPlan.features === 'string' ? JSON.stringify(currentPlan.features.split(',').map(f => f.trim())) : currentPlan.features
            };

            if (isEdit) {
                await superAdminApiClient.put(`/superadmin/plans/${currentPlan.id}`, payload);
                toast.success("Plan updated successfully");
            } else {
                await superAdminApiClient.post('/superadmin/plans', payload);
                toast.success("Plan created successfully");
            }
            setShowModal(false);
            fetchPlans();
        } catch (error) {
            toast.error("Error saving plan");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this plan?")) {
            try {
                await superAdminApiClient.delete(`/superadmin/plans/${id}`);
                toast.success("Plan deleted");
                fetchPlans();
            } catch (error) {
                toast.error("Error deleting plan");
            }
        }
    };

    const openModal = (plan = null) => {
        if (plan) {
            setCurrentPlan({
                ...plan,
                features: Array.isArray(JSON.parse(plan.features)) ? JSON.parse(plan.features).join(', ') : plan.features
            });
            setIsEdit(true);
        } else {
            setCurrentPlan({
                name: '',
                price: '',
                duration_days: 30,
                is_trial: false,
                features: '',
                status: 'active',
                leads_included: ''
            });
            setIsEdit(false);
        }
        setValidated(false);
        setShowModal(true);
    };

    return (
        <div className="">
            <div className="">
                <div className="row">
                    <div className="col-12">
                        <div className="page-title-box d-sm-flex align-items-center justify-content-between m-0 mb-2">
                            <h4 className="mb-sm-0">Subscription Plans</h4>
                            <div className="page-title-right">
                                <button className="btn btn-primary" onClick={() => openModal()}>
                                    <i className="ri-add-line align-bottom me-1"></i> Add New Plan
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-xl-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="table-responsive">
                                    <Table className="table align-middle table-nowrap mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Plan Name</th>
                                                <th>Price (₹)</th>
                                                <th>Duration (Days)</th>
                                                <th>Trial?</th>
                                                <th>Included Leads</th>
                                                <th>Status</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr><td colSpan="6" className="text-center">Loading...</td></tr>
                                            ) : plans.map((plan) => (
                                                <tr key={plan.id}>
                                                    <td>{plan.name}</td>
                                                    <td>₹{formatPrice(plan.price)}</td>
                                                    <td>{plan.duration_days}</td>
                                                    <td>{plan.is_trial ? <span className="badge bg-success">Yes</span> : <span className="badge bg-secondary">No</span>}</td>
                                                    <td>{plan.leads_included}</td>
                                                    <td>
                                                        <span className={`badge ${plan.status === 'active' ? 'bg-success' : 'bg-danger'}`}>
                                                            {plan.status.toUpperCase()}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            <button className="btn btn-sm btn-soft-info" onClick={() => openModal(plan)}>
                                                                <i className="ri-pencil-fill"></i>
                                                            </button>
                                                            <button className="btn btn-sm btn-soft-danger" onClick={() => handleDelete(plan.id)}>
                                                                <i className="ri-delete-bin-fill"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{isEdit ? 'Edit Plan' : 'Add New Plan'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Plan Name <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                value={currentPlan.name}
                                onChange={(e) => setCurrentPlan({ ...currentPlan, name: e.target.value })}
                                placeholder="Enter plan name"
                                required
                                isInvalid={validated && !currentPlan.name}
                            />
                            <Form.Control.Feedback type="invalid">Plan name is required</Form.Control.Feedback>
                        </Form.Group>
                        <div className="row">
                            <div className="col-md-4">
                                <Form.Group className="mb-3">
                                    <Form.Label>Price (₹) <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={currentPlan.price}
                                        onChange={(e) => setCurrentPlan({ ...currentPlan, price: e.target.value })}
                                        required
                                        isInvalid={validated && (currentPlan.price === '' || currentPlan.price === null)}
                                    />
                                    <Form.Control.Feedback type="invalid">Price is required</Form.Control.Feedback>
                                </Form.Group>
                            </div>
                            <div className="col-md-4">
                                <Form.Group className="mb-3">
                                    <Form.Label>Duration (Days) <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={currentPlan.duration_days}
                                        onChange={(e) => setCurrentPlan({ ...currentPlan, duration_days: e.target.value })}
                                        required
                                        isInvalid={validated && !currentPlan.duration_days}
                                    />
                                    <Form.Control.Feedback type="invalid">Duration is required</Form.Control.Feedback>
                                </Form.Group>
                            </div>
                            <div className="col-md-4">
                                <Form.Group className="mb-3">
                                    <Form.Label>Included Leads <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={currentPlan.leads_included}
                                        onChange={(e) => setCurrentPlan({ ...currentPlan, leads_included: e.target.value })}
                                        required
                                        isInvalid={validated && (currentPlan.leads_included === '' || currentPlan.leads_included === null)}
                                    />
                                    <Form.Control.Feedback type="invalid">Included leads is required</Form.Control.Feedback>
                                </Form.Group>
                            </div>
                        </div>
                        <Form.Group className="mb-3">
                            <Form.Label>Features (Comma separated)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={currentPlan.features}
                                onChange={(e) => setCurrentPlan({ ...currentPlan, features: e.target.value })}
                                placeholder="e.g. Lead Management, Support, Analytics"
                            />
                        </Form.Group>
                        <div className="d-flex gap-4">
                            <Form.Check
                                type="checkbox"
                                label="Mark as Trial"
                                checked={currentPlan.is_trial}
                                onChange={(e) => setCurrentPlan({ ...currentPlan, is_trial: e.target.checked })}
                            />
                            <Form.Group>
                                <Form.Check
                                    type="switch"
                                    id="status-switch"
                                    label="Active Status"
                                    checked={currentPlan.status === 'active'}
                                    onChange={(e) => setCurrentPlan({ ...currentPlan, status: e.target.checked ? 'active' : 'inactive' })}
                                />
                            </Form.Group>
                        </div>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
                    <Button variant="primary" onClick={handleSave}>Save Plan</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PlanListing;
